import { FC } from "react"
import classNames from "classnames"
import CellActions from "./CellActions"
import CellDescription from "./CellDescription"
import { useCellCardState } from "./useCellCardState"
import { CellCardProps, UserStatus } from "."
import { Icon } from "@/components/shared/Icon"
import Tooltip from "@/components/shared/Tooltip"
import { useTruncation } from "@/hooks/useTruncation"
import { getCellIconName, getCellIconTestId } from "@/utils/cells"
import { renderWithTooltip } from "@/utils/index"

const CellCardContent: FC<CellCardProps> = ({
   cellId,
   isCustom = false,
   hasDoorSensor = false,
   doorSensorLowBattery = false,
   userStatus,
   maintenance,
   hideButtons,
   occupied,
   cellStatus: cell,
   typefaceColor,
   bpm,
   bpmStabilityScore,
   rpm,
   rpmStabilityScore,
   className,
   onSetVacant,
   onSetMaintenanceOff,
   onViewDetails,
   onTriggerTestAlarm,
}) => {
   const {
      cellStatus,
      typefaceIconColor,
      hideVitals,
      noVitals,
      humanPresenceInVacant,
   } = useCellCardState({
      cellStatus: cell,
      occupied,
      maintenance,
      typefaceColor,
      userStatus,
   })
   const [cellIdRef, isCellIdTruncated] = useTruncation<HTMLSpanElement>(cellId)

   const iconName = getCellIconName(
      isCustom,
      hasDoorSensor,
      doorSensorLowBattery
   )

   const cellIcon = (
      <Icon
         className={classNames({
            "cell-card__door-sensor-battery-icon": doorSensorLowBattery,
         })}
         size="16"
         color={typefaceIconColor}
         name={iconName}
         data-testid={getCellIconTestId(iconName)}
      />
   )

   return (
      <div className={classNames("cell-card__content", className)}>
         <div
            className={classNames("cell-card__header", {
               "cell-card__header--moving": userStatus === UserStatus.MOVING,
            })}
         >
            <span className="cell-card__cell-id-wrapper">
               {doorSensorLowBattery ? (
                  <Tooltip content="Low battery in door sensor">
                     {cellIcon}
                  </Tooltip>
               ) : (
                  cellIcon
               )}
               {renderWithTooltip(
                  cellIdRef,
                  "cell-card__cell-id",
                  cellId,
                  isCellIdTruncated
               )}
            </span>
            <CellActions
               noVitals={noVitals}
               maintenance={maintenance}
               hideButtons={hideButtons}
               onSetVacant={onSetVacant}
               onSetMaintenanceOff={onSetMaintenanceOff}
               onViewDetails={onViewDetails}
            />
         </div>
         <CellDescription
            cellStatus={cellStatus}
            userStatus={userStatus}
            noVitals={noVitals}
            humanPresenceInVacant={humanPresenceInVacant}
            {...(!noVitals && bpm && { bpm })}
            bpmStabilityScore={bpmStabilityScore}
            {...(!noVitals && rpm && { rpm })}
            rpmStabilityScore={rpmStabilityScore}
            hideVitals={hideVitals}
            typefaceIconColor={typefaceIconColor}
            onTriggerTestAlarm={onTriggerTestAlarm}
         />
      </div>
   )
}

export default CellCardContent
